<template>
    <div class="kv-component-root">
        <span v-if="hasTrigger" class="kv-trigger-wrapper">
            <slot name="trigger" :toggle="toggleShow"></slot>
        </span>

        <div :id="id" class="kv-modal" :class="{ 'is-open': isOpen }">
            <div class="kv-modal-wrapper" :class="'kv-modal-' + normalizedSize">
                <div class="kv-modal-dialog">

                    <div class="kv-modal-header">
                        <button v-if="showClose" class="kv-modal-close" @click.stop.prevent="close">
                            <Icon icon-class="ui-x-close" />
                        </button>
                        <div v-if="$slots.title || $slots.subtitle" class="d-flex flex-column gap-sm">
                            <KvTypography v-if="$slots.title" size="huge">
                                <slot name="title"></slot>
                            </KvTypography>

                            <KvTypography v-if="$slots.subtitle" size="medium" color="muted">
                                <slot name="subtitle"></slot>
                            </KvTypography>
                        </div>

                        <slot name="header" :toggle="toggleShow"></slot>
                    </div>

                    <div class="kv-modal-body kv-scrollbar">
                        <slot name="body" :toggle="toggleShow"></slot>
                    </div>

                    <div class="kv-modal-footer">
                        <slot name="footer" :toggle="toggleShow"></slot>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import KvButton from '../form/kv-button.vue';
import Icon from '../icon/icon.vue';
import KvTypography from '../ui/kv-typography.vue';

export default {
    name: "modal",
    components: {
        KvButton,
        KvTypography,
        Icon
    },
    props: {
        id: String,
        modelValue: {
            type: Boolean,
            default: false
        },
        showClose: {
            type: Boolean,
            default: true,
        },
        size: {
            type: String,
            default: "regular",
            validator(value) {
                return ["regular", "small", "sm", "extra-small", "xs", "large", "lg", "extra-large", "xl"].includes(value);
            }
        }
    },
    data() {
        return {
            isOpen: this.modelValue
        };
    },
    computed: {
        // Verifica se existe conteúdo no slot trigger (compatível com Vue 2 e 3)
        hasTrigger() {
            return !!this.$scopedSlots.trigger || !!this.$slots.trigger;
        },
        normalizedSize() {
            const map = {
                "": "regular", "sm": "small", "xs": "extra-small", "lg": "large", "xl": "extra-large",
            };
            return map[this.size] || this.size;
        }
    },
    watch: {
        modelValue(val) { this.isOpen = val; },
        isOpen(val) { this.$emit("update:modelValue", val); }
    },
    methods: {
        toggleShow(forceState) {
            this.isOpen = typeof forceState === "boolean" ? forceState : !this.isOpen;
        },
        open() {
            this.isOpen = true;
            this.$emit("open");
        },
        close() {
            this.isOpen = false;
            this.$emit("close");
        },
    }
};
</script>

<style scoped lang="less">
// Breakpoint para mobile
@mobile-breakpoint: 768px;

.kv-component-root {
    display: contents;
}

@supports not (display: contents) {
    .kv-component-root {
        display: inline-block;
    }
}

.kv-modal-close {
    position: absolute;
    right: 10px;
    top: 10px;
    background: transparent;
    border: none;
    font-weight: 400;
}

.kv-modal {
    display: flex;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    justify-content: center;
    align-items: center;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity .22s ease, visibility 0s linear .22s;
    z-index: 1050;
}

.kv-modal.is-open {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transition: opacity .22s ease;
}

.kv-modal-wrapper {
    display: grid;
    grid-template-rows: auto 1fr;
    gap: 10px;
    opacity: 0;
    transform: translateY(12px);
    transition: transform .24s cubic-bezier(.16, 1, .3, 1), opacity .2s ease;
    width: 100%;
    max-height: 95%;
    position: relative;
    max-width: 800px; // Default regular

    // No mobile, força o modal a ter uma margem lateral (width 95%)
    // mas respeita o max-width se o modal for do tipo "xs" ou "sm"
    @media (max-width: @mobile-breakpoint) {
        width: 95%;
    }

    &.kv-modal-extra-small {
        max-width: 250px;
    }

    &.kv-modal-small {
        max-width: 500px;
    }

    &.kv-modal-large {
        max-width: 1000px;
    }

    &.kv-modal-extra-large {
        max-width: 1200px;
    }
}

.kv-modal.is-open .kv-modal-wrapper {
    opacity: 1;
    transform: none;
}

.kv-modal-dialog {
    background: #FCFCFD;
    border: 1px solid #E5E7EB;
    border-radius: 24px;
    display: flex;
    flex-direction: column;
    height: auto;
    min-height: 0;
    position: relative;
    max-height: calc(100dvh - 50px);
    overflow: hidden;

    .kv-modal-header {
        padding: 24px 24px 0 24px;
        text-align: center;
        flex: none;
        
        @media (max-width: @mobile-breakpoint) {
            padding: 16px 16px 0 16px;
        }
    }

    .kv-modal-body {
        padding: 24px;
        overflow-y: auto;
        overflow-x: hidden;
        flex: 1 1 auto;
        min-height: 0;
        -webkit-overflow-scrolling: touch;

        @media (max-width: @mobile-breakpoint) {
            padding: 16px;
        }
    }

    .kv-modal-footer {
        padding: 0 24px 24px 24px;
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        flex: none;
        background: #FCFCFD;

        @media (max-width: @mobile-breakpoint) {
            padding: 0 16px 16px 16px;
        }
    }
}

@keyframes fadeInUp {
    from {
        transform: translateY(20px);
        opacity: 0;
    }

    to {
        transform: translateY(0);
        opacity: 1;
    }
}
</style>